"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import {
  AccountBadge,
  Crumbs,
  Page,
  ToggleChip,
} from "@/components/ui";
import { HttpError, PortfolioSummaryMultitradeRow, api } from "@/lib/api";
import { fmtRupeesShort, pnlClass } from "@/lib/fmt";
import { isIncompleteAccount } from "@/lib/dataQuality";

// Four cycle × account grids on the same page, bucketed by trading system:
//
//   1. Multitrade — Multitrade trading system (MTrust multitrade dealers).
//   2. Kanhoji    — Kanhoji trading system (XTS-routed fills).
//   3. Others     — every OTHER system (Noren/Garuda/iTrader/XTS_NonKanhoji, …)
//                   summed together.
//   4. Combined   — ALL trading systems; sums per-cycle to mv_pnl_by_cycle.
//
// The buckets RECONCILE: Multitrade + Kanhoji + Others == Combined per
// (account, cycle) — post chakra_schema#72 the per-system split is a
// long-grain MV (mv_pnl_by_cycle_system) that rows the trading_system
// dimension, and SUM over all systems ties to the combined column exactly.
// "Others" is the fourth bucket that closes the gap the old two-grid split
// left open. Rows are keyed by cycle_label (NIFTY monthly cycle);
// cross-exchange positions bucket into the NIFTY cycle they sit inside per
// GLOSSARY.md § cycle alignment.

// Hairline row styling — matches the shared DataTable / data-freshness look
// (Thead: border-b border-rule; row cells: border-b border-rule/50; no
// vertical gridlines). border-separate + border-spacing-0 is retained because
// the sticky first column needs it (see <Table sticky> in ui.tsx); per-cell
// border-b then draws the row hairlines cleanly.
const CELL_BASE = "py-1.5 pr-4 text-sm num whitespace-nowrap border-b border-rule/50";
const HEADER_BASE = "py-1.5 pr-4 eyebrow font-normal text-mute whitespace-nowrap border-b border-rule";

type AccountMeta = {
  account: string; account_id: number; broker: string;
  is_client_account: boolean; clientcode: string | null;
};

function PnlByCycleGrid({
  title, subtitle, rows, csvFilenamePrefix, pinnedAccounts,
}: {
  title: string;
  subtitle: string;
  rows: PortfolioSummaryMultitradeRow[] | null;
  csvFilenamePrefix: string;
  // Fixed column set + order. Every grid on the page uses the same
  // pinned list so columns line up left-to-right; a grid with no data
  // for a given account still shows the column with all "—" cells.
  pinnedAccounts: AccountMeta[] | null;
}) {
  const grid = useMemo(() => {
    if (!pinnedAccounts) return null;

    // Row keys: cycle_label sorted by cycle_end desc.
    const rowKeyMap = new Map<string, {
      key: string; cycle_label: string; cycle_end: string;
    }>();
    for (const r of rows || []) {
      const key = r.cycle_label;
      if (!rowKeyMap.has(key)) {
        rowKeyMap.set(key, {
          key,
          cycle_label: r.cycle_label,
          cycle_end: r.cycle_end,
        });
      }
    }
    const rowKeys = [...rowKeyMap.values()].sort((a, b) =>
      b.cycle_end.localeCompare(a.cycle_end)
    );

    // Cell lookup keyed on (cycle_label :: account)
    const cells = new Map<string, number>();
    for (const r of rows || []) {
      cells.set(`${r.cycle_label}::${r.account}`, Number(r.pnl_net_rs));
    }

    // Lifetime per pinned account (Σ pnl_net_rs across all rows in THIS
    // grid's data). Zero when the account has no rows in this trading system.
    const lifetimeByAccount = new Map<string, number>();
    for (const a of pinnedAccounts) lifetimeByAccount.set(a.account, 0);
    for (const r of rows || []) {
      lifetimeByAccount.set(
        r.account,
        (lifetimeByAccount.get(r.account) ?? 0) + Number(r.pnl_net_rs),
      );
    }
    const accounts = pinnedAccounts.map((a) => ({
      ...a,
      lifetime: lifetimeByAccount.get(a.account) ?? 0,
    }));

    // Σ row totals per rowKey (across pinned accounts).
    const rowTotals = new Map<string, number>();
    for (const rk of rowKeys) {
      let sum = 0;
      for (const a of accounts) {
        const v = cells.get(`${rk.cycle_label}::${a.account}`);
        if (v !== undefined) sum += v;
      }
      rowTotals.set(rk.key, sum);
    }
    const grand = accounts.reduce((s, a) => s + a.lifetime, 0);

    return { rowKeys, accounts, cells, rowTotals, grand };
  }, [rows, pinnedAccounts]);

  function downloadCsv(csv: string, filename: string) {
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const el = document.createElement("a");
    el.href = url;
    el.download = filename;
    document.body.appendChild(el);
    el.click();
    document.body.removeChild(el);
    URL.revokeObjectURL(url);
  }

  function exportCsv() {
    if (!grid) return;
    const { rowKeys, accounts, cells, rowTotals, grand } = grid;
    const header = ["Cycle", ...accounts.map((a) => a.account), "Row total"].join(",");
    const lines = rowKeys.map((rk) => {
      const cs = accounts.map((a) => {
        const v = cells.get(`${rk.cycle_label}::${a.account}`);
        return v === undefined ? "" : String(v);
      });
      return [rk.cycle_label, ...cs, String(rowTotals.get(rk.key) ?? 0)].join(",");
    });
    const footer = ["Lifetime", ...accounts.map((a) => String(a.lifetime)), String(grand)].join(",");
    const csv = [header, ...lines, footer].join("\n");
    downloadCsv(csv, `${csvFilenamePrefix}-${new Date().toISOString().slice(0, 10)}.csv`);
  }

  if (rows === null) {
    return (
      <div className="mb-10">
        <div className="mb-2 text-sm font-semibold">{title}</div>
        <p className="eyebrow text-mute">Loading…</p>
      </div>
    );
  }
  if (!grid) {
    return (
      <div className="mb-10">
        <div className="mb-2 text-sm font-semibold">{title}</div>
        <p className="eyebrow text-mute">No activity.</p>
      </div>
    );
  }

  return (
    <div className="mb-10">
      <div className="mb-2 flex flex-wrap items-baseline gap-3">
        <div className="text-sm font-semibold">{title}</div>
        <div className="eyebrow text-mute">{subtitle}</div>
        <button
          type="button"
          onClick={exportCsv}
          className="eyebrow text-mute transition hover:text-foreground"
        >
          ↳ Export CSV
        </button>
        <div className="ml-auto eyebrow text-mute">
          {grid.rowKeys.length} rows × {grid.accounts.length} accounts
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="table-fixed border-separate border-spacing-0">
          <colgroup>
            <col style={{ width: "8rem" }} />
            {grid.accounts.map((a) => (
              <col key={a.account} style={{ width: "7rem" }} />
            ))}
            <col style={{ width: "7rem" }} />
          </colgroup>
          <thead>
            <tr>
              <th className={`${HEADER_BASE} text-left sticky left-0 bg-background z-10`}>
                NIFTY expiry cycle
              </th>
              {grid.accounts.map((a) => (
                <th key={a.account} className={`${HEADER_BASE} text-right`}>
                  <div className="flex flex-col items-end gap-1">
                    <Link href={`/accounts/${a.account_id}`} className="hover:text-foreground">
                      {a.account}
                    </Link>
                    <AccountBadge
                      is_client_account={a.is_client_account}
                      clientcode={a.clientcode} accountId={a.account_id}
                    />
                    {/* #135 — incomplete-data marker, this column only */}
                    {isIncompleteAccount(a.account) && (
                      <span
                        className="text-[10px] font-normal tracking-wide text-down"
                        title="Some active trading days are missing for this account — figures may be incomplete."
                      >
                        ⚠ incomplete
                      </span>
                    )}
                  </div>
                </th>
              ))}
              <th className={`${HEADER_BASE} text-right`}>Σ row</th>
            </tr>
          </thead>
          <tbody>
            {grid.rowKeys.map((rk) => (
              <tr key={rk.key}>
                <th
                  scope="row"
                  className={`${CELL_BASE} text-left font-normal text-mute sticky left-0 bg-background`}
                >
                  {rk.cycle_label}
                </th>
                {grid.accounts.map((a) => {
                  const v = grid.cells.get(`${rk.cycle_label}::${a.account}`);
                  return (
                    <td
                      key={a.account}
                      className={`${CELL_BASE} text-right ${
                        v === undefined ? "text-mute" : pnlClass(v)
                      }`}
                    >
                      {v === undefined ? "—" : fmtRupeesShort(v)}
                    </td>
                  );
                })}
                <td
                  className={`${CELL_BASE} text-right ${pnlClass(
                    grid.rowTotals.get(rk.key) ?? 0,
                  )}`}
                >
                  {fmtRupeesShort(grid.rowTotals.get(rk.key) ?? 0)}
                </td>
              </tr>
            ))}
            <tr>
              <th
                scope="row"
                className={`${CELL_BASE} text-left font-semibold sticky left-0 bg-background`}
              >
                Σ lifetime
              </th>
              {grid.accounts.map((a) => (
                <td
                  key={a.account}
                  className={`${CELL_BASE} text-right font-semibold ${pnlClass(a.lifetime)}`}
                >
                  {fmtRupeesShort(a.lifetime)}
                </td>
              ))}
              <td className={`${CELL_BASE} text-right font-semibold ${pnlClass(grid.grand)}`}>
                {fmtRupeesShort(grid.grand)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function PortfolioSummaryMultitradePage() {
  const [multitradeRows, setMultitradeRows] = useState<PortfolioSummaryMultitradeRow[] | null>(null);
  const [kanhojiRows, setKanhojiRows]         = useState<PortfolioSummaryMultitradeRow[] | null>(null);
  const [othersRows, setOthersRows]           = useState<PortfolioSummaryMultitradeRow[] | null>(null);
  const [combinedRows, setCombinedRows]       = useState<PortfolioSummaryMultitradeRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [activeOnly, setActiveOnly] = useState(true);

  useEffect(() => {
    setErr(null);
    setMultitradeRows(null);
    setKanhojiRows(null);
    setOthersRows(null);
    setCombinedRows(null);
    Promise.all([
      api.portfolioSummaryMultitrade(activeOnly),
      api.portfolioSummaryKanhoji(activeOnly),
      api.portfolioSummaryOthers(activeOnly),
      api.portfolioSummaryCombined(activeOnly),
    ])
      .then(([mt, kn, ot, cb]) => {
        setMultitradeRows(mt);
        setKanhojiRows(kn);
        setOthersRows(ot);
        setCombinedRows(cb);
      })
      .catch((e) => {
        if (e instanceof HttpError && e.status === 403) setErr("Admin-only report.");
        else setErr(String(e));
      });
  }, [activeOnly]);

  // Union of accounts across all three datasets, sorted by Combined
  // lifetime (best-first). Combined ⊇ Multitrade ∪ Kanhoji, so it's the
  // natural "master" list, but we union anyway to be safe if a future
  // account shows up in one trading system but not the aggregate for whatever
  // reason. Same column order used by every grid → cross-grid values
  // for the same account are directly comparable side-by-side.
  const pinnedAccounts = useMemo<AccountMeta[] | null>(() => {
    if (!multitradeRows || !kanhojiRows || !othersRows || !combinedRows) return null;
    const byName = new Map<string, AccountMeta>();
    const combinedLifetime = new Map<string, number>();
    for (const src of [multitradeRows, kanhojiRows, othersRows, combinedRows]) {
      for (const r of src) {
        if (!byName.has(r.account)) {
          byName.set(r.account, {
            account: r.account, account_id: r.account_id, broker: r.broker,
            is_client_account: r.is_client_account, clientcode: r.clientcode,
          });
        }
      }
    }
    for (const r of combinedRows) {
      combinedLifetime.set(
        r.account,
        (combinedLifetime.get(r.account) ?? 0) + Number(r.pnl_net_rs),
      );
    }
    return [...byName.values()].sort(
      (a, b) => (combinedLifetime.get(b.account) ?? 0) - (combinedLifetime.get(a.account) ?? 0)
    );
  }, [multitradeRows, kanhojiRows, othersRows, combinedRows]);

  if (err) return <p className="text-sm text-down">{err}</p>;

  return (
    <Page
      eyebrow={<Crumbs items={[{ label: "Reports" }, { label: "Portfolio summary (Split)" }]} />}
      title={<>Portfolio <em className="italic">summary</em> — Multitrade/Kanhoji split</>}
    >
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <ToggleChip
          selected={activeOnly}
          onClick={() => setActiveOnly((v) => !v)}
        >
          Active only
        </ToggleChip>
        <div className="ml-auto eyebrow text-mute">
          Per (account, NIFTY expiry cycle) — Multitrade + Kanhoji + Others = Combined.
        </div>
      </div>

      <PnlByCycleGrid
        title="1. Multitrade"
        subtitle="P&L (₹) — Multitrade trading system only (MTrust multitrade dealers)"
        rows={multitradeRows}
        pinnedAccounts={pinnedAccounts}
        csvFilenamePrefix="portfolio-summary-multitrade"
      />
      <PnlByCycleGrid
        title="2. Kanhoji"
        subtitle="P&L (₹) — Kanhoji only (XTS-routed fills)"
        rows={kanhojiRows}
        pinnedAccounts={pinnedAccounts}
        csvFilenamePrefix="portfolio-summary-kanhoji"
      />
      <PnlByCycleGrid
        title="3. Others"
        subtitle="P&L (₹) — all systems except Multitrade/Kanhoji (Noren/Garuda/iTrader/XTS_NonKanhoji, …)"
        rows={othersRows}
        pinnedAccounts={pinnedAccounts}
        csvFilenamePrefix="portfolio-summary-others"
      />
      <PnlByCycleGrid
        title="4. Combined"
        subtitle="P&L (₹) — all systems; sums to mv_pnl_by_cycle (= Multitrade + Kanhoji + Others)"
        rows={combinedRows}
        pinnedAccounts={pinnedAccounts}
        csvFilenamePrefix="portfolio-summary-combined"
      />
    </Page>
  );
}
